
import React, { useState, useEffect, useMemo } from 'react';
import { AppNode, WorkflowNodeData } from '../../types';
import * as LucideIcons from 'lucide-react';
import { Settings2, X, Code, Mail, Database, Bot, GitFork, List, Users, Clock, Radio, Zap, Workflow, Layout, AlertCircle } from 'lucide-react';
import { PropertyField } from '../molecules/PropertyField';
import { useEditorConfig } from '../../contexts/EditorContext';
import { TabConfig } from '../../utils/propertyPanelRegistry';
import { VariableDefinition } from '../../utils/variableExtraction';

interface NodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNode: AppNode | null;
  updateNodeData: (id: string, data: Partial<WorkflowNodeData>) => void;
  variableContext?: VariableDefinition[];
  activeTabId: string;
  setActiveTabId: (id: string) => void;
}

const NodeModal: React.FC<NodeModalProps> = ({ 
    isOpen, 
    onClose, 
    selectedNode, 
    updateNodeData, 
    variableContext = [],
    activeTabId,
    setActiveTabId
}) => {
  const { nodeTypes, propertyRegistry, componentRegistry } = useEditorConfig();
  const [localData, setLocalData] = useState<WorkflowNodeData>({ label: '' });
  
  const [viewMode, setViewMode] = useState<'form' | 'code'>('form');
  const [jsonCode, setJsonCode] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedNode && isOpen) {
      setLocalData(selectedNode.data);
      setViewMode('form');
      setJsonError(null);
    }
  }, [selectedNode, isOpen]);

  const config = useMemo(() => selectedNode ? nodeTypes[selectedNode.type || ''] : null, [selectedNode, nodeTypes]);
  const nodeConfig = useMemo(() => selectedNode ? (propertyRegistry[selectedNode.type || ''] || { type: 'default', tabs: [{ id: 'properties', label: 'General', fields: [] }] }) : null, [selectedNode, propertyRegistry]);

  if (!isOpen || !selectedNode || !config || !nodeConfig) return null;

  const handleChange = (key: string, value: any) => {
    setLocalData((prev) => ({ ...prev, [key]: value }));
  };

  const toggleViewMode = () => {
      if (viewMode === 'form') {
          setJsonCode(JSON.stringify(localData, null, 2));
          setViewMode('code');
          setJsonError(null);
      } else {
          try {
              const parsed = JSON.parse(jsonCode);
              setLocalData(parsed);
              setViewMode('form');
              setJsonError(null);
          } catch (e: any) {
              setJsonError("Invalid JSON: " + e.message);
          }
      }
  };

  const handleSave = () => {
      if (viewMode === 'code') {
          try {
              const parsed = JSON.parse(jsonCode);
              updateNodeData(selectedNode.id, parsed);
              onClose();
          } catch (e: any) {
              setJsonError("Cannot save: Invalid JSON");
          }
      } else {
          updateNodeData(selectedNode.id, localData);
          onClose();
      }
  };

  const renderIcon = (iconName: string) => {
      switch(iconName) {
          case 'properties': return <Settings2 size={16} />;
          case 'form': return <List size={16} />;
          case 'assignments': return <Users size={16} />;
          case 'script': return <Code size={16} />;
          case 'http': return <LucideIcons.Globe size={16} />;
          case 'email': return <Mail size={16} />;
          case 'llm': return <Bot size={16} />;
          case 'routing': case 'conditions': return <GitFork size={16} />;
          case 'timer': return <Clock size={16} />;
          case 'message': return <Mail size={16} />;
          case 'signal': return <Zap size={16} />;
          case 'database': return <Database size={16} />;
          case 'call': return <Workflow size={16} />;
          case 'fileimport': return <LucideIcons.Upload size={16} />;
          case 'extraction': return <LucideIcons.Filter size={16} />;
          default:
              const DynIcon = (LucideIcons as any)[iconName] || (LucideIcons as any)[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
              return DynIcon ? <DynIcon size={16} /> : <Settings2 size={16} />;
      }
  };

  const shouldRenderField = (field: any) => {
      if (!field.condition) return true;
      const { field: targetField, operator, value } = field.condition;
      const targetValue = localData[targetField];
      
      switch (operator) {
          case 'equals': return targetValue === value;
          case 'notEquals': return targetValue !== value;
          case 'contains': return Array.isArray(targetValue) && targetValue.includes(value);
          default: return true;
      }
  };

  const renderTabContent = (tab: TabConfig) => {
      if (tab.specialComponent && componentRegistry[tab.specialComponent]) {
          const Component = componentRegistry[tab.specialComponent];
          return <Component 
            data={localData} 
            onChange={handleChange} 
            nodeType={selectedNode.type} 
            context={variableContext} 
          />;
      }
      
      return (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {tab.fields.map(field => {
                  if (!shouldRenderField(field)) return null;
                  const isWide = field.type === 'textarea' || field.type === 'object' || field.type === 'array' || field.type === 'expression';
                  return (
                    <div key={field.name} className={isWide ? 'md:col-span-12' : 'md:col-span-6'}>
                        <PropertyField
                            config={field}
                            value={localData[field.name]}
                            onChange={handleChange}
                        />
                    </div>
                  );
              })}
          </div>
      );
  };
  
  const ConfigIcon = (config.icon ? (LucideIcons[config.icon as keyof typeof LucideIcons] as LucideIcons.LucideIcon || Settings2) : Settings2);

  const activeTab = nodeConfig.tabs.find(t => t.id === activeTabId) || nodeConfig.tabs[0];
  const isFullscreen = activeTab?.layout === 'fullscreen';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
       <div className="bg-white dark:bg-slate-950 fixed inset-4 md:inset-8 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200">
          
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0">
             <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700" style={{ color: config.color }}>
                    <ConfigIcon size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
                        {config.label}
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700 uppercase tracking-wider">
                            {selectedNode.type}
                        </span>
                    </h2>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-400 font-mono">Key: {selectedNode.id}</span>
                    </div>
                </div>
             </div>
             
             <div className="flex items-center gap-2">
                 <button
                    onClick={toggleViewMode}
                    className={`p-2 rounded-lg transition-colors border ${viewMode === 'code' ? 'bg-teal-50 border-teal-200 text-teal-600 dark:bg-teal-900/20 dark:border-teal-800 dark:text-teal-400' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400'}`}
                    title={viewMode === 'form' ? "Switch to JSON Code" : "Switch to Form View"}
                 >
                     {viewMode === 'form' ? <Code size={20} /> : <Layout size={20} />}
                 </button>
                 <button 
                    onClick={onClose} 
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                 >
                     <X size={20} />
                 </button>
             </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
             {viewMode === 'form' ? (
                 <>
                    <div className={`
                        bg-slate-50 dark:bg-slate-900/50 border-r border-slate-200 dark:border-slate-800 
                        overflow-y-auto p-3 space-y-1 shrink-0 transition-all duration-300 ease-in-out
                        ${isFullscreen ? 'w-16 items-center px-2' : 'w-64'}
                    `}>
                        {!isFullscreen && (
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3 mt-2">
                                Configuration
                            </div>
                        )}
                        {nodeConfig.tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTabId(tab.id)}
                                title={isFullscreen ? tab.label : undefined}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group
                                    ${activeTabId === tab.id 
                                        ? 'bg-white dark:bg-slate-800 text-teal-700 dark:text-teal-400 shadow-sm ring-1 ring-slate-200 dark:ring-slate-700' 
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900'
                                    }
                                    ${isFullscreen ? 'justify-center px-0' : ''}
                                `}
                            >
                                <span className={`opacity-70 group-hover:opacity-100 ${activeTabId === tab.id ? 'text-teal-600' : ''}`}>
                                    {renderIcon(tab.id)}
                                </span>
                                {!isFullscreen && tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto bg-white dark:bg-slate-950 relative">
                        <div className={`mx-auto h-full flex flex-col ${isFullscreen ? 'max-w-none' : 'max-w-4xl'}`}>
                            {nodeConfig.tabs.map(tab => {
                                if (tab.id !== activeTabId) return null;
                                return (
                                    <div key={tab.id} className="animate-in fade-in slide-in-from-bottom-2 duration-300 h-full flex flex-col">
                                        {!tab.specialComponent ? (
                                            <div className="p-8">
                                                <div className="mb-6 pb-4 border-b border-slate-100 dark:border-slate-900 shrink-0">
                                                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1">{tab.label}</h3>
                                                    <p className="text-sm text-slate-500">Configure {tab.label.toLowerCase()} settings.</p>
                                                </div>
                                                {renderTabContent(tab)}
                                            </div>
                                        ) : (
                                            <div className="flex-1 min-h-0">
                                                {renderTabContent(tab)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                 </>
             ) : (
                 <div className="flex-1 flex flex-col bg-white dark:bg-slate-950 relative">
                     <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                         <span className="font-mono">JSON Source</span>
                         {jsonError ? (
                             <span className="text-red-500 dark:text-red-400 flex items-center gap-1 font-bold">
                                 <AlertCircle size={12}/> {jsonError}
                             </span>
                         ) : (
                             <span className="text-emerald-600 dark:text-emerald-500">Valid JSON</span>
                         )}
                     </div>
                     <textarea
                        value={jsonCode}
                        onChange={(e) => {
                            setJsonCode(e.target.value);
                            try {
                                JSON.parse(e.target.value);
                                setJsonError(null);
                            } catch (err: any) {
                            }
                        }}
                        className="flex-1 w-full p-4 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-300 font-mono text-sm outline-none resize-none"
                        spellCheck={false}
                     />
                 </div>
             )}
          </div>
          
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-end gap-3 shrink-0">
                <button 
                    onClick={onClose} 
                    className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded-lg transition-all"
                >
                    Cancel
                </button>
                <button 
                    onClick={handleSave} 
                    disabled={viewMode === 'code' && !!jsonError}
                    className="px-5 py-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm shadow-teal-600/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Save Configuration
                </button>
          </div>
       </div>
    </div>
  );
};

export default NodeModal;
